/**
 * Voice-activated barge-in orchestration (Phase B).
 *
 * While the coach is speaking and hands-free mode is on, run the barge VAD; when
 * the user starts speaking, fire `onBargeIn` (which stops playback and starts
 * listening). Web-only today (the VAD is a no-op on native, which keeps
 * tap-to-interrupt). Fully inert when `enabled` is false.
 */
import { useEffect, useRef } from "react";

import { BARGE_VAD_SUPPORTED, startBargeVad } from "./barge-vad";
import { subscribePlayback } from "./coach-playback";

export const VOICE_ACTIVATED_SUPPORTED = BARGE_VAD_SUPPORTED;

export function useVoiceActivatedBargeIn(opts: {
  enabled: boolean;
  onBargeIn: () => void;
}): void {
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });

  useEffect(() => {
    if (!BARGE_VAD_SUPPORTED) return;
    let stopVad: (() => void) | null = null;
    let cancelled = false;

    const stop = () => {
      stopVad?.();
      stopVad = null;
    };

    const unsub = subscribePlayback((playing) => {
      if (playing && optsRef.current.enabled && !stopVad) {
        void startBargeVad(() => {
          stopVad = null;
          optsRef.current.onBargeIn();
        }).then((cleanup) => {
          if (cancelled) cleanup();
          else stopVad = cleanup;
        });
      } else if (!playing) {
        stop();
      }
    });

    return () => {
      cancelled = true;
      unsub();
      stop();
    };
  }, []);
}
